// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the Share button on the home screen, third in the top
 * inline-start column under Home and Show anchor (it was "Share
 * dataset…" in the Tools panel's View tab until 2026-10-03).
 *
 * Always there: with a dataset loaded it shares that dataset's link
 * (`/dataset/<slug>`); with none it shares the site. The system share
 * sheet where there is one, the clipboard otherwise (`shareService`).
 */

import { t } from '../i18n'

export interface ShareButtonDeps {
  /** The dataset on the primary globe, or null. */
  currentDataset(): { id: string; title: string; slug?: string } | null
  announce?(message: string): void
}

/** Wire `#share-btn`. Safe to call twice. */
export function initShareButton(deps: ShareButtonDeps): void {
  const button = document.getElementById('share-btn')
  if (!button || button.dataset.wired === 'true') return
  button.dataset.wired = 'true'
  button.addEventListener('click', async () => {
    const { shareDataset, buildDatasetShareUrl } = await import('../services/shareService')
    const dataset = deps.currentDataset()
    const shared = dataset
      ? await shareDataset({
        title: dataset.title,
        text: t('tools.share.text', { title: dataset.title }),
        url: buildDatasetShareUrl(dataset),
      })
      : await shareDataset({
        title: t('share.site.title'),
        text: t('share.site.text'),
        url: window.location.origin,
      })
    if (!shared) return
    deps.announce?.(t(dataset ? 'tools.announce.shared' : 'share.site.shared'))
    // Without a share sheet the link went to the clipboard, which shows
    // nothing on its own; say so beside the button for a moment.
    if (!navigator.share) showCopied(button)
  })
}

let copiedTimer: number | null = null

function showCopied(button: HTMLElement): void {
  let note = document.getElementById('share-btn-note')
  if (!note) {
    note = document.createElement('span')
    note.id = 'share-btn-note'
    note.setAttribute('aria-hidden', 'true')
    button.insertAdjacentElement('afterend', note)
  }
  note.textContent = t('share.copied')
  note.hidden = false
  if (copiedTimer !== null) window.clearTimeout(copiedTimer)
  copiedTimer = window.setTimeout(() => {
    if (note) note.hidden = true
    copiedTimer = null
  }, 2200)
}
